import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Heart, MessageCircle } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AdminLayout from "@/layouts/admin-layout"
import { typedPatch } from "@/lib/typed-router"
import type { AdminUpdateUser } from "@/types/router-mutations"
import type { AdminUsersShow } from "@/types/serializers"

type Props = AdminUsersShow

export default function AdminUserShow({ user, recent_posts }: Props) {
  const isSuspended = !!user.suspended_at

  function handleSuspend() {
    typedPatch<AdminUpdateUser>(`/admin/users/${user.id}`, {
      user: { suspended: "true" },
    })
  }

  function handleUnsuspend() {
    typedPatch<AdminUpdateUser>(`/admin/users/${user.id}`, {
      user: { suspended: "false" },
    })
  }

  return (
    <AdminLayout>
      <Head title={`${user.full_name} | Admin KrownFT`} />

      <div className="space-y-6">
        <Link
          href="/admin/users"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver a Usuarios
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-foreground text-2xl font-bold">
              {user.full_name}
            </h1>
            <p className="text-muted-foreground">@{user.nickname}</p>
          </div>
          <div className="flex items-center gap-2">
            {isSuspended ? (
              <Button variant="outline" onClick={handleUnsuspend}>
                Reactivar Usuario
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleSuspend}>
                Suspender Usuario
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Información del Usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Email</span>
                <span className="text-foreground text-sm">{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Rol</span>
                <Badge variant="secondary">{user.role}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">
                  Verificado
                </span>
                <Badge
                  variant={user.verified ? "default" : "outline"}
                  className={
                    user.verified
                      ? "bg-green-500/20 text-green-400"
                      : "text-muted-foreground"
                  }
                >
                  {user.verified ? "Sí" : "No"}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Estado</span>
                {isSuspended ? (
                  <Badge variant="destructive">Suspendido</Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="border-green-400/30 text-green-400"
                  >
                    Activo
                  </Badge>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">
                  Registrado
                </span>
                <span className="text-foreground text-sm">
                  {new Date(user.created_at).toLocaleDateString("es-AR")}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Actividad Reciente ({recent_posts.length} publicaciones)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recent_posts.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Sin publicaciones recientes
                </p>
              ) : (
                <div className="space-y-3">
                  {recent_posts.map((post) => (
                    <div
                      key={post.id}
                      className="border-border rounded-lg border p-3"
                    >
                      <p className="text-foreground line-clamp-2 text-sm">
                        {post.body}
                      </p>
                      <div className="text-muted-foreground mt-2 flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1">
                          <Heart className="h-3 w-3" />
                          {post.likes_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" />
                          {post.comments_count}
                        </span>
                        <span>
                          {new Date(post.created_at).toLocaleDateString(
                            "es-AR",
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
